import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Star, Search, Plus } from "lucide-react";

import { useUserContext } from "../../../contexts/UserContext";
import Loader from "../../UI/Loader/Loader";
import ErrorAlert from "../../UI/Alert/ErrorAlert";
import { permission } from "../../../config/permission";
import DeleteTealiumIntegrationModal from "./DeleteIntegrationModal";
import { formatDate } from "../../../utils/converter";
import { updateSpecificTealiumProduct } from "../../../utils/APIs";
import { toast } from "react-toastify";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import { getAgGridSortStorageKey } from "../../UI/AgGrid";
import { Card, Button, Input, AgTableCard } from "../../UI";

const IntegrationTable = () => {
  const navigate = useNavigate();
  const { userData, checkPermission, updateTealiumIntegrations } = useUserContext();
  const { enums, content } = useContentfulContext();

  const [deleteIntegration, setDeleteIntegration] = useState(false);
  const [tealiumData, setTealiumData] = useState([]);
  const [loading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [handleId, setHandleId] = useState("");
  const [, setConfirmModal] = useState({
    active: false,
    deactivate: false,
    delete: false,
  });
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setTealiumData(userData.tealiumIntegrations || []);
  }, [userData.defaultTealiumKey, userData.tealiumIntegrations]);

  const [gridApi, setGridApi] = useState(null);

  const onFilterTextBoxChanged = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (gridApi) {
      gridApi.setFilterModel({
        tealium_api_name: val ? { type: "contains", filter: val.toLowerCase() } : null,
      });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    if (gridApi) {
      gridApi.setFilterModel({});
    }
  };

  const handleSetAsDefault = async (id) => {
    try {
      setActionLoading(true);
      await updateSpecificTealiumProduct(
        userData.token,
        userData.selectedOrganization.org_token,
        { productId: id, bodyData: { default: true } }
      );
      updateTealiumIntegrations();
      toast.success(content.TealiumApiKey_CONTENT.success.update);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const colDefs = [
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.tealium_api_name,
      field: "tealium_api_name",
      cellRenderer: ({ data }) => (
        <button
          type="button"
          className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
          onClick={() => navigate(`/setup/tealium/edit/${data._id}`)}
        >
          {data.tealium_api_name}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
    },
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.tealium_account,
      field: "tealium_account",
      filter: true,
      sortable: true,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="text-stride-body text-sm font-medium">{value || "—"}</span>
      ),
    },
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.tealium_profile,
      field: "tealium_profile",
      filter: true,
      sortable: true,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-mono">{value || "—"}</span>
      ),
    },
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.tealium_environment,
      field: "tealium_environment",
      filter: true,
      sortable: true,
      suppressMovable: true,
      cellRenderer: ({ value }) => (
        <span className="stride-badge-pill bg-primary-soft text-primary text-xs font-semibold">{value || "—"}</span>
      ),
    },
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.default,
      field: "default",
      sortable: false,
      suppressMovable: true,
      cellRenderer: ({ data }) => {
        const isDefault = data.default === true || data.default === "true";
        return isDefault ? (
          <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
            Default
          </span>
        ) : (
          <span className="text-stride-muted text-xs">—</span>
        );
      },
    },
    {
      headerName: content.TealiumApiKey_CONTENT.integration_table_headers.updated_at,
      field: "updatedAt",
      cellRenderer: ({ data }) => (
        <span className="text-stride-muted text-xs">
          {formatDate(data.updatedAt)}
        </span>
      ),
      comparator: (date1, date2) => new Date(date1) - new Date(date2),
      sortable: true,
      suppressMovable: true,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ({ data }) => {
        const canEdit = checkPermission(permission.EDIT_TEALIUM);
        const canDelete = checkPermission(permission.DELETE_TEALIUM);
        const canSetDefault = checkPermission(permission.EDIT_TEALIUM_OP_EDIT_DEFAULT);
        const isDefault = data.default === true || data.default === "true";

        return (
          <div className="stride-table-actions">
            {canEdit && (
              <button
                type="button"
                title="Edit Integration"
                className="stride-table-btn stride-table-btn-edit"
                onClick={() => navigate(`/setup/tealium/edit/${data._id}`)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {canSetDefault && !isDefault && (
              <button
                type="button"
                title="Set as default"
                className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                onClick={() => handleSetAsDefault(data._id)}
              >
                <Star className="w-3.5 h-3.5 text-amber-500" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title="Delete Integration"
                className="stride-table-btn stride-table-btn-delete"
                onClick={() => {
                  setHandleId(data._id);
                  setDeleteIntegration(true);
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);

    const storageKey = getAgGridSortStorageKey("tealium-integration-table");
    const sortModel = JSON.parse(localStorage.getItem(storageKey));

    if (!sortModel || sortModel?.length === 0) {
      params.columnApi.applyColumnState({
        state: [{ colId: "updatedAt", sort: "desc" }],
        applyOrder: false,
      });
    }
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "tealium_api_name", minWidth: 220 },
        { colId: "tealium_account", minWidth: 160 },
        { colId: "tealium_profile", minWidth: 140 },
        { colId: "tealium_environment", minWidth: 120 },
        { colId: "default", minWidth: 100 },
        { colId: "updatedAt", minWidth: 140 },
        { colId: "actions", minWidth: 90 },
      ],
    };
  }, []);

  return (
    <div className="flex flex-col space-y-6">
      {error && (
        <ErrorAlert timeout={content.TealiumApiKey_CONTENT.timeout}>{error}</ErrorAlert>
      )}

      {/* Empty State */}
      {!loading && (!tealiumData || tealiumData.length === 0) ? (
        <Card className="text-center p-12 max-w-lg mx-auto">
          <p className="text-base text-stride-heading font-medium mb-4">
            No Tealium API integrations configured yet.
          </p>
          {checkPermission(permission.CREATE_TEALIUM) && (
            <Button
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => navigate("/setup/tealium-new-integration")}
            >
              {content.TealiumApiKey_CONTENT.new_integration_cta_text}
            </Button>
          )}
        </Card>
      ) : (
        <AgTableCard
          header={
            <>
              <div className="w-full md:w-80">
                <Input
                  placeholder="Search integration name..."
                  value={searchTerm}
                  onChange={onFilterTextBoxChanged}
                  prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                  clearable
                  onClear={handleClearSearch}
                />
              </div>

              {checkPermission(permission.CREATE_TEALIUM) && (
                <Button
                  variant="primary"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate("/setup/tealium-new-integration")}
                >
                  {content.TealiumApiKey_CONTENT.new_integration_cta_text}
                </Button>
              )}
            </>
          }
          tableId="tealium-integration-table"
          loading={loading}
          rowData={tealiumData}
          columnDefs={colDefs}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
          rowSelection="multiple"
          suppressRowClickSelection
          pagination={true}
          paginationPageSize={content.TealiumApiKey_CONTENT.default_page_size}
          paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
        />
      )}

      {actionLoading && <Loader />}

      {deleteIntegration && (
        <DeleteTealiumIntegrationModal
          setConfirmModal={setConfirmModal}
          id={handleId}
          setDeleteIntegration={setDeleteIntegration}
          setActionLoading={setActionLoading}
          getTealiumData={updateTealiumIntegrations}
        />
      )}
    </div>
  );
};

export default IntegrationTable;
